// src/utils/secretGuard.js
// ARTICHAUT ne doit jamais conserver de secret d'accès (mot de passe, clé privée, phrase de récupération).
// Ce contrôle simple repère les saisies à risque dans les champs d'instructions des œuvres numériques.
// Niveau « block » : saisie qui ressemble clairement à un secret → enregistrement refusé.
// Niveau « warn » : suite de 12 à 24 mots courts, qui peut être une phrase de récupération → confirmation demandée.

const KEYWORD_VALUE =
  /(mot de passe|mdp|password|passphrase|phrase (?:de )?(?:récup\w*|recup\w*|secrète|secrete)|recovery phrase|seed(?: phrase)?|clé privée|cle privee|private key|code pin)\s*[:=]\s*\S{4,}/i;
const HEX_KEY = /\b(?:0x)?[0-9a-fA-F]{64}\b/;
const WIF_KEY = /\b[5KL][1-9A-HJ-NP-Za-km-z]{50,51}\b/;
const SEED_LIKE = /(?:\b[a-z]{3,8} ){11,23}\b[a-z]{3,8}\b/g;

// Mots courants : une phrase ordinaire en contient plusieurs, une phrase de récupération presque jamais.
const STOPWORDS = new Set([
  "de", "la", "le", "les", "des", "du", "un", "une", "et", "au", "aux", "en", "pour", "par", "sur", "avec", "dans",
  "qui", "que", "est", "son", "sa", "ses", "ce", "cette", "il", "elle", "ne", "pas", "ou", "the", "and", "for", "with",
  "of", "to", "in", "is", "on", "by", "or",
]);

export function secretLevel(text) {
  const value = String(text || "");
  if (!value) return null;
  if (KEYWORD_VALUE.test(value) || HEX_KEY.test(value) || WIF_KEY.test(value)) return "block";

  const lower = value.toLowerCase();
  const matches = lower.match(SEED_LIKE) || [];
  for (const m of matches) {
    const words = m.split(" ");
    const common = words.filter((w) => STOPWORDS.has(w)).length;
    if (common < 3) return "warn";
  }
  return null;
}

const DIGITAL_TEXT_KEYS = [
  "format",
  "usage_rights",
  "resale_conditions",
  "transmission_conditions",
  "display_equipment",
  "access_custodian",
  "access_procedure",
  "heir_instructions",
];

// Contrôle des champs patrimoniaux et numériques d'une fiche avant enregistrement.
// Renvoie { error } (à afficher, enregistrement refusé), { warn } (confirmation demandée) ou {}.
export function validateHeritageFields(data) {
  if (!data) return {};

  const source = String(data.external_source || "").trim();
  const ref = String(data.external_id || "").trim();
  if (Boolean(source) !== Boolean(ref)) {
    return { error: "Renseignez la plateforme source et l'identifiant externe ensemble, ou laissez les deux vides." };
  }

  const digital = data.digital_asset && typeof data.digital_asset === "object" ? data.digital_asset : null;
  if (!digital) return {};

  let warn = false;
  for (const key of DIGITAL_TEXT_KEYS) {
    const level = secretLevel(digital[key]);
    if (level === "block") {
      return {
        error:
          "Un champ de l'œuvre numérique semble contenir un mot de passe, une clé ou une phrase de récupération. " +
          "ARTICHAUT ne doit jamais les conserver : indiquez seulement où se trouve l'accès, qui le détient et la procédure à suivre.",
      };
    }
    if (level === "warn") warn = true;
  }
  return warn
    ? {
        warn:
          "Un champ ressemble à une suite de mots pouvant être une phrase de récupération. " +
          "ARTICHAUT ne doit jamais la conserver. Enregistrer quand même ?",
      }
    : {};
}
